import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { IItem } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import useDonation from 'state/order/hooks/use-donation';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getEntryPriceOrZero } from 'state/unified-menu/utils';
import { logger } from 'utils/logger';

import { DonationAtCheckout } from '../donation-at-checkout';
import { IDonationAtCheckoutProps } from '../donation-at-checkout/types';

interface IDonationContainerProps {
  isLoading: boolean;
}

const useDonationItems = () => {
  const { featureDonation, loading } = useFeatureDonation();
  const { getOneMenuItemAvailability, checkItemAvailability, pricingFunction } = useMenuContext();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const [donationItems, setDonationItems] = useState<IItem[]>([]);
  const [isCheckingDonationItemsAvailability, setIsCheckingDonationItemsAvailability] = useState(
    true
  );

  useEffect(() => {
    if (loading) {
      setIsCheckingDonationItemsAvailability(true);
      return;
    }

    if (isUnifiedMenuEnabled) {
      const items =
        featureDonation?.donationItemList?.filter(
          item =>
            item &&
            getOneMenuItemAvailability(item._id).availabilityStatus ===
              ItemAvailabilityStatus.AVAILABLE
        ) ?? [];

      setDonationItems(items as IItem[]);
      setIsCheckingDonationItemsAvailability(false);
    } else {
      let isMounted = true;
      setIsCheckingDonationItemsAvailability(true);

      (async () => {
        try {
          const list = featureDonation?.donationItemList ?? [];
          const availabilityFlags = await Promise.all(
            list.map(async item => {
              if (!item) {
                return false;
              }
              // in order to be able to use the same availability check function for
              // legacy menu, we need to add the 'item-' prefix to the item id
              const itemId = !item._id.includes('item-') ? `item-${item._id}` : item._id;
              const availabilityStatus = (await checkItemAvailability(itemId)).availabilityStatus;
              return availabilityStatus === ItemAvailabilityStatus.AVAILABLE;
            })
          );
          const items = list.filter((item, idx) => {
            if (!availabilityFlags[idx]) {
              return false;
            }
            const price = pricingFunction({ item: (item as unknown) as IItem });
            return price > 0;
          });

          if (!isMounted) {
            return;
          }

          setDonationItems((items as unknown) as IItem[]);
        } catch (error) {
          logger.error({
            error,
            message: 'Error checking donation items availability',
            context: { featureDonation },
          });
        } finally {
          if (isMounted) {
            setIsCheckingDonationItemsAvailability(false);
          }
        }
      })();

      return () => {
        isMounted = false;
      };
    }
  }, [
    checkItemAvailability,
    featureDonation,
    featureDonation?.donationItemList,
    getOneMenuItemAvailability,
    isUnifiedMenuEnabled,
    loading,
    pricingFunction,
  ]);

  return { donationItems, loading: loading || isCheckingDonationItemsAvailability };
};

const DonationContainer = ({ isLoading }: IDonationContainerProps) => {
  const { featureDonation } = useFeatureDonation();
  const { posServiceMode } = useDerivedServiceModeContext();
  const { indexedMenu } = useUnifiedMenuContext();
  const { pricingFunction } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { donationItems, loading: isDonationOptionsLoading } = useDonationItems();

  const getLegacyDonationPrice = useCallback((item: IItem) => pricingFunction({ item }), [
    pricingFunction,
  ]);

  const getDonationPrice = useCallback(
    (item: IItem) => {
      if (!isUnifiedMenuEnabled) {
        return getLegacyDonationPrice(item);
      }

      const menuItem = indexedMenu.find(item._id);
      if (!menuItem) {
        return 0;
      }

      return getEntryPriceOrZero(menuItem, posServiceMode);
    },
    [getLegacyDonationPrice, indexedMenu, isUnifiedMenuEnabled, posServiceMode]
  );

  const {
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
    selectedDonationCartEntry,
  } = useDonation({
    getDonationPrice,
    validDonationItemIds: donationItems.map(item => item._id),
    isDonationOptionsLoaded: !isDonationOptionsLoading,
  });

  const allDonationOptions: IDonationAtCheckoutProps['donationOptions'] = useMemo(
    () => [
      ...(donationItems.map(item => ({
        label: formatCurrencyForLocale(getDonationPrice(item)),
        value: item._id,
        isDisabled: false,
      })) as IDonationAtCheckoutProps['donationOptions']),
    ],
    [donationItems, formatCurrencyForLocale, getDonationPrice]
  );

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId: string) => {
      if (selectedDonationCartEntry?._id === newSelectedDonationId) {
        handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        return;
      }

      const optionItem = donationItems.find(item => item?._id === newSelectedDonationId);
      if (!optionItem) {
        return;
      }
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
    ]
  );

  const haveAllItemsZeroPrice = (donationItems: IItem[]): boolean => {
    const total = donationItems.reduce(
      (accumulator, currentItem) => accumulator + getDonationPrice(currentItem),
      0
    );

    return total <= 0;
  };

  useEffectOnce(() => {
    if (donationItems.length) {
      logRBIEvent({
        name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
        type: EventTypes.Other,
        attributes: {},
      });
    }
  });

  if (!donationItems.length || haveAllItemsZeroPrice(donationItems)) {
    return null;
  }

  return (
    <DonationAtCheckout
      isLoading={isLoading}
      header={featureDonation?.header?.locale ?? ''}
      subtitle={featureDonation?.subtitle?.locale ?? ''}
      donationOptions={allDonationOptions}
      selectedDonationId={selectedDonationCartEntry?._id}
      onChangeValue={handleDonationToggleChange}
    />
  );
};

export default DonationContainer;
